import {
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  type AnimationType,
} from '../../project/ProjectDocument.js'
import { MAX_FINITE_FLOAT32 } from '../particles/ParticleBuffer.js'
import { MAX_PARTICLE_COUNT } from '../particles/particleBudget.js'

const TWO_PI = Math.PI * 2
const UINT32_RANGE = 4_294_967_296
const MAX_UINT32 = 0xffff_ffff
// Keep the established travel-mode energy while decoupling it from the idle
// wiggle control. A user's dampening choice must never erase a named path.
const TRAVEL_PATH_STRENGTH = 0.95

/**
 * Kernel-supported animation modes. The ten timeline modes are unioned locally
 * so this kernel stays self-consistent while the shared schema rollout lands;
 * once ProjectDocument.ANIMATION_TYPES includes them, this union collapses
 * onto AnimationType without changing call-site assignability.
 */
export type KernelAnimationMode =
  | AnimationType
  | 'pen'
  | 'dots'
  | 'words'
  | 'bleed'
  | 'sandstorm'
  | 'magnet'
  | 'rain'
  | 'breathing'
  | 'shimmer'
  | 'flow'

export interface AnimationSampleInput {
  mode: KernelAnimationMode
  easedProgress: number
  particleIndex: number
  particleCount: number
  orderX: number
  orderY: number
  chaosX: number
  chaosY: number
  seed: number
  timeSeconds: number
  canvasWidth: number
  canvasHeight: number
  /** Seeded ambient oscillation rate in cycles per logical second. */
  wiggleSpeed: number
  /** Seeded ambient oscillation amplitude in logical canvas pixels. */
  wiggleForce: number
  /**
   * Ambient-wiggle retention: 0 suppresses idle wiggle and 1 preserves its
   * full configured amplitude. Named travel and living paths are independent.
   */
  wiggleDampening: number
  /**
   * Layout word index owning this particle, or -1 when unknown. Word index
   * doubles as RTL reading-order rank: 0 is the first word read (rightmost).
   */
  ownerWord: number
  /** Layout letter index owning this particle, or -1 when unknown. */
  ownerLetter: number
  /** Layout dot index, or -1; >= 0 means this particle IS dot/diacritic ink. */
  ownerDot: number
  /** Total layout words, >= 0. */
  wordCount: number
  /** Total layout letters, >= 0. */
  letterCount: number
  /** Logical center of the laid-out ink. Falls back to the canvas center. */
  textCenterX?: number
  /** Logical center of the laid-out ink. Falls back to the canvas center. */
  textCenterY?: number
  /** Optional local glyph/letter tangent used by the Flow mode. */
  tangentX?: number
  /** Optional local glyph/letter tangent used by the Flow mode. */
  tangentY?: number
}

export interface MutableAnimationSample {
  x: number
  y: number
  /** Per-particle order progress after mode scheduling. */
  progress: number
  /**
   * Per-particle reveal/highlight alpha. For backward compatibility the
   * kernel writes this only when the caller initializes an `alpha` slot.
   */
  alpha?: number
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function assertAnimationType(mode: KernelAnimationMode): void {
  switch (mode) {
    case 'linear':
    case 'spiral':
    case 'wave':
    case 'bounce':
    case 'cascade':
    case 'vortex':
    case 'gravity':
    case 'scatter':
    case 'elastic':
    case 'typewriter':
    case 'pen':
    case 'dots':
    case 'words':
    case 'bleed':
    case 'sandstorm':
    case 'magnet':
    case 'rain':
    case 'breathing':
    case 'shimmer':
    case 'flow':
      return
    default:
      throw new RangeError(`Unknown animation mode: ${String(mode)}`)
  }
}

function assertFiniteFloat32(value: number, label: string): void {
  if (!Number.isFinite(value) || Math.abs(value) > MAX_FINITE_FLOAT32) {
    throw new RangeError(`${label} must be finite and Float32-safe`)
  }
}

function validateAnimationInput(input: AnimationSampleInput): void {
  assertAnimationType(input.mode)
  assertFiniteFloat32(input.easedProgress, 'Animation progress')
  assertFiniteFloat32(input.orderX, 'Order X')
  assertFiniteFloat32(input.orderY, 'Order Y')
  assertFiniteFloat32(input.chaosX, 'Chaos X')
  assertFiniteFloat32(input.chaosY, 'Chaos Y')
  assertFiniteFloat32(input.timeSeconds, 'Animation time')
  if (input.textCenterX !== undefined) assertFiniteFloat32(input.textCenterX, 'Text center X')
  if (input.textCenterY !== undefined) assertFiniteFloat32(input.textCenterY, 'Text center Y')
  if (input.tangentX !== undefined) assertFiniteFloat32(input.tangentX, 'Animation tangent X')
  if (input.tangentY !== undefined) assertFiniteFloat32(input.tangentY, 'Animation tangent Y')

  if (
    !Number.isFinite(input.canvasWidth)
    || !Number.isFinite(input.canvasHeight)
    || input.canvasWidth <= 0
    || input.canvasHeight <= 0
    || input.canvasWidth > MAX_CANVAS_DIMENSION
    || input.canvasHeight > MAX_CANVAS_DIMENSION
    || input.canvasWidth * input.canvasHeight > MAX_CANVAS_PIXEL_AREA
  ) {
    throw new RangeError('Animation canvas dimensions exceed the logical canvas budget')
  }
  if (
    !Number.isFinite(input.wiggleDampening)
    || input.wiggleDampening < 0
    || input.wiggleDampening > 1
  ) {
    throw new RangeError('Animation dampening must be finite and within 0…1')
  }
  if (!Number.isFinite(input.wiggleSpeed) || input.wiggleSpeed < 0 || input.wiggleSpeed > 20) {
    throw new RangeError('Animation wiggle speed must be finite and within 0…20')
  }
  if (!Number.isFinite(input.wiggleForce) || input.wiggleForce < 0 || input.wiggleForce > 20) {
    throw new RangeError('Animation wiggle force must be finite and within 0…20')
  }
  if (!Number.isSafeInteger(input.seed) || input.seed < 0 || input.seed > MAX_UINT32) {
    throw new RangeError('Animation seed must be a Uint32 integer')
  }
  if (
    !Number.isSafeInteger(input.particleCount)
    || input.particleCount < 1
    || input.particleCount > MAX_PARTICLE_COUNT
  ) {
    throw new RangeError(`Particle count must be an integer within 1…${MAX_PARTICLE_COUNT}`)
  }
  if (
    !Number.isSafeInteger(input.particleIndex)
    || input.particleIndex < 0
    || input.particleIndex >= input.particleCount
  ) {
    throw new RangeError('Particle index must be an integer within the active particle count')
  }
  if (!Number.isSafeInteger(input.ownerWord) || input.ownerWord < -1) {
    throw new RangeError('Animation owner word must be an integer of -1 or greater')
  }
  if (!Number.isSafeInteger(input.ownerLetter) || input.ownerLetter < -1) {
    throw new RangeError('Animation owner letter must be an integer of -1 or greater')
  }
  if (!Number.isSafeInteger(input.ownerDot) || input.ownerDot < -1) {
    throw new RangeError('Animation owner dot must be an integer of -1 or greater')
  }
  if (!Number.isSafeInteger(input.wordCount) || input.wordCount < 0) {
    throw new RangeError('Animation word count must be an integer of 0 or greater')
  }
  if (!Number.isSafeInteger(input.letterCount) || input.letterCount < 0) {
    throw new RangeError('Animation letter count must be an integer of 0 or greater')
  }
}

function safeLerp(start: number, end: number, progress: number): number {
  return start * (1 - progress) + end * progress
}

function smoothstep(progress: number): number {
  return progress * progress * (3 - 2 * progress)
}

/**
 * Blend weight for secondary paths that replace the chaos or order endpoint.
 * The weight ramps in from 0 and back out to 0 across the outer quarters so
 * the replaced path enters and exits continuously while the exact endpoint
 * early exits keep progress 0 and 1 pixel-exact.
 */
function edgeRetention(progress: number): number {
  const edgeProgress = progress < 0.25
    ? progress / 0.25
    : progress > 0.75
      ? (1 - progress) / 0.25
      : 1
  return TRAVEL_PATH_STRENGTH * smoothstep(edgeProgress)
}

function isRevealMode(mode: KernelAnimationMode): boolean {
  return mode === 'typewriter' || mode === 'pen' || mode === 'dots' || mode === 'words'
}

function isLivingMode(mode: KernelAnimationMode): boolean {
  return mode === 'breathing' || mode === 'shimmer' || mode === 'flow'
}

function writeAlpha(out: MutableAnimationSample, alpha: number): void {
  // Existing portable/runtime callers created the original three-field shape.
  // Opt-in writing avoids silently changing their object contract until they
  // are upgraded, while new renderers can initialize `alpha: 1` immediately.
  if ('alpha' in out) out.alpha = alpha
}

function unitHash(seed: number, particleIndex: number, salt: number): number {
  let value = (seed >>> 0) ^ Math.imul((particleIndex | 0) + 1, 0x9e37_79b1) ^ salt
  value = Math.imul(value ^ (value >>> 16), 0x85eb_ca6b)
  value = Math.imul(value ^ (value >>> 13), 0xc2b2_ae35)
  value = (value ^ (value >>> 16)) >>> 0
  return value / UINT32_RANGE
}

function bounceOut(progress: number): number {
  const scale = 7.5625
  const divisor = 2.75
  if (progress < 1 / divisor) return scale * progress * progress
  if (progress < 2 / divisor) {
    const shifted = progress - 1.5 / divisor
    return scale * shifted * shifted + 0.75
  }
  if (progress < 2.5 / divisor) {
    const shifted = progress - 2.25 / divisor
    return scale * shifted * shifted + 0.9375
  }
  const shifted = progress - 2.625 / divisor
  return scale * shifted * shifted + 0.984375
}

function dampedSpring(progress: number): number {
  const angularFrequency = 8
  const dampingRatio = 0.3
  const dampedFrequency = angularFrequency * Math.sqrt(1 - dampingRatio * dampingRatio)
  return 1 - Math.exp(-dampingRatio * angularFrequency * progress)
    * Math.cos(dampedFrequency * progress)
}

/**
 * Sample one particle without allocating. The caller owns both input and output;
 * the kernel uses only scalar locals and deterministic seed/index hashing.
 */
export function sampleAnimation(
  input: AnimationSampleInput,
  out: MutableAnimationSample,
): void {
  // Public runtime data is preflighted before endpoint writes so failures leave
  // caller-owned output byte-for-byte untouched.
  validateAnimationInput(input)

  const easedProgress = input.easedProgress
  const progress = clamp01(easedProgress)
  const easingOvershoot = easedProgress - progress

  // Exact input endpoints remain exact. Living modes deliberately continue at
  // the ordered endpoint, so they can breathe/flow/shimmer while playback is
  // resting instead of freezing into the same still frame as Linear.
  if (easedProgress === 0) {
    out.x = input.chaosX
    out.y = input.chaosY
    out.progress = 0
    writeAlpha(out, isRevealMode(input.mode) ? 0 : 1)
    return
  }
  const ambientWiggle = input.wiggleForce * input.wiggleDampening
  if (easedProgress === 1 && ambientWiggle === 0 && !isLivingMode(input.mode)) {
    out.x = input.orderX
    out.y = input.orderY
    out.progress = 1
    writeAlpha(out, 1)
    return
  }

  const canvasWidth = input.canvasWidth
  const canvasHeight = input.canvasHeight
  const timeSeconds = input.timeSeconds
  const particleIndex = input.particleIndex
  let delayRandom = 0
  let phase = 0
  let secondaryEnvelope = 0

  // Hash only modes that consume the corresponding deterministic value.
  switch (input.mode) {
    case 'scatter':
    case 'gravity':
    case 'sandstorm':
    case 'rain':
      delayRandom = unitHash(input.seed, particleIndex, 0x68bc_21eb)
      break
    case 'spiral':
    case 'wave':
    case 'vortex':
    case 'magnet':
    case 'breathing':
    case 'shimmer':
    case 'flow':
      phase = unitHash(input.seed, particleIndex, 0x02e5_be93) * TWO_PI
      break
    case 'linear':
    case 'bounce':
    case 'cascade':
    case 'elastic':
    case 'typewriter':
    case 'pen':
    case 'dots':
    case 'words':
    case 'bleed':
      break
  }

  let localProgress = progress
  let alpha = 1
  switch (input.mode) {
    case 'cascade': {
      const rightToLeftPosition = 1 - clamp01(input.orderX / canvasWidth)
      const delay = rightToLeftPosition * 0.65
      localProgress = clamp01((progress - delay) / (1 - delay))
      break
    }
    case 'wave': {
      secondaryEnvelope = Math.sin(Math.PI * progress) * TRAVEL_PATH_STRENGTH
      const wavePhase = input.orderX / 100 + input.orderY / 100 + timeSeconds * 0.5 + phase
      localProgress = clamp01(
        progress + Math.sin(wavePhase) * 0.18 * secondaryEnvelope,
      )
      break
    }
    case 'scatter': {
      const delay = delayRandom * 0.6
      localProgress = clamp01((progress - delay) / (1 - delay))
      break
    }
    case 'typewriter': {
      // Reveal letters in actual RTL reading order when layout ownership is
      // available. The positional fallback is also RTL and avoids tying the
      // apparent typing direction to raster scan order.
      const rtlPosition = 1 - clamp01(input.orderX / canvasWidth)
      let readingRank = rtlPosition
      if (input.ownerLetter >= 0 && input.letterCount > 0) {
        readingRank = Math.min(input.ownerLetter, input.letterCount - 1)
          / Math.max(1, input.letterCount - 1)
      } else if (input.ownerWord >= 0 && input.wordCount > 0) {
        readingRank = Math.min(input.ownerWord, input.wordCount - 1)
          / Math.max(1, input.wordCount - 1)
      }
      // Ownership-aware typing uses overlapping letter windows so it reads as
      // individual glyph arrivals rather than collapsing into whole-word
      // blocks at the midpoint. The metadata-free fallback keeps its compact
      // positional sweep for compatibility.
      const hasLetterOwnership = input.ownerLetter >= 0 && input.letterCount > 0
      const revealWindow = hasLetterOwnership ? 0.28 : 0.18
      const delay = readingRank * (1 - revealWindow)
      localProgress = smoothstep(clamp01((progress - delay) / revealWindow))
      alpha = clamp01(localProgress)
      break
    }
    case 'bounce':
      localProgress = bounceOut(progress)
      break
    case 'elastic':
      localProgress = dampedSpring(progress)
      break
    case 'pen': {
      // Qalam sweep: this is intentionally described as an ownership-aware
      // ink reveal rather than a false claim of glyph-outline tracing. Words
      // and letters lead in Persian reading order; X orders ink within them.
      const normalizedOrderX = clamp01(input.orderX / canvasWidth)
      const wordRank = input.ownerWord >= 0 && input.wordCount > 0
        ? Math.min(input.ownerWord, input.wordCount - 1) / Math.max(1, input.wordCount - 1)
        : 0
      const letterRank = input.ownerLetter >= 0 && input.letterCount > 0
        ? Math.min(input.ownerLetter, input.letterCount - 1) / Math.max(1, input.letterCount - 1)
        : 0
      const hasOwnership = input.ownerWord >= 0 || input.ownerLetter >= 0
      const delay = hasOwnership
        ? wordRank * 0.5 + letterRank * 0.2 + (1 - normalizedOrderX) * 0.14
        : (1 - normalizedOrderX) * 0.84
      localProgress = smoothstep(clamp01((progress - delay) / 0.16))
      alpha = clamp01(localProgress)
      break
    }
    case 'dots': {
      // Body ink assembles first; dots and diacritics bloom near the end. Both
      // use alpha so unrevealed ink does not linger visibly at chaos positions.
      if (input.ownerDot >= 0) {
        const dotStagger = unitHash(input.seed, particleIndex, 0x1b56_c4e9) * 0.08
        localProgress = smoothstep(clamp01((progress - 0.72 - dotStagger) / 0.2))
      } else {
        localProgress = smoothstep(clamp01(progress / 0.72))
      }
      alpha = clamp01(localProgress)
      break
    }
    case 'words': {
      // Crisp per-word windows [ownerWord/wordCount, (ownerWord+1)/wordCount].
      // Without owner metadata, fall back to positional right-to-left windows.
      if (input.ownerWord >= 0 && input.wordCount > 0) {
        const wordIndex = Math.min(input.ownerWord, input.wordCount - 1)
        localProgress = smoothstep(clamp01(
          (progress - wordIndex / input.wordCount) * input.wordCount,
        ))
      } else {
        const normalizedOrderX = clamp01(input.orderX / canvasWidth)
        const delay = (1 - normalizedOrderX) * 0.8
        localProgress = smoothstep(clamp01((progress - delay) / 0.2))
      }
      alpha = clamp01(localProgress)
      break
    }
    case 'linear':
    case 'spiral':
    case 'vortex':
    case 'gravity':
    case 'bleed':
    case 'magnet':
    case 'rain':
    case 'breathing':
    case 'shimmer':
    case 'flow':
      break
    case 'sandstorm': {
      // Wind-borne ink: seeded stagger like scatter, while the secondary wind
      // path below replaces the chaos start with a downwind displacement.
      const delay = delayRandom * 0.45
      localProgress = clamp01((progress - delay) / (1 - delay))
      break
    }
  }

  if (easedProgress === 1) localProgress = 1
  else localProgress += easingOvershoot

  const baseX = safeLerp(input.chaosX, input.orderX, localProgress)
  const baseY = safeLerp(input.chaosY, input.orderY, localProgress)
  let x = baseX
  let y = baseY

  switch (input.mode) {
    case 'pen': {
      // Qalam pressure leaves a subtle seeded nib impression while the sweep
      // is in flight. This prevents the ownership schedule from degenerating
      // into the same binary field as Words/Typewriter on common two-word
      // layouts, while fading cleanly at both travel endpoints.
      const nibVariation = unitHash(input.seed, particleIndex, 0x9e6c_63d1)
      const pressure = Math.sin(Math.PI * progress) * clamp01(alpha)
      x += (nibVariation - 0.5) * pressure * 3
      y -= (1 + nibVariation * 2) * pressure * 2
      break
    }
    case 'spiral': {
      secondaryEnvelope = Math.sin(Math.PI * progress) * TRAVEL_PATH_STRENGTH
      const radius = Math.min(canvasWidth, canvasHeight) * 0.18 * secondaryEnvelope
      const angle = phase + timeSeconds * 4 + localProgress * Math.PI * 3
      x += Math.cos(angle) * radius
      y += Math.sin(angle) * radius
      break
    }
    case 'wave': {
      const angle = input.orderX / 80 + timeSeconds * 2 + phase
      y += Math.sin(angle) * Math.min(canvasWidth, canvasHeight) * 0.025 * secondaryEnvelope
      break
    }
    case 'bounce':
      secondaryEnvelope = Math.sin(Math.PI * progress) * TRAVEL_PATH_STRENGTH
      y -= Math.abs(Math.sin(localProgress * TWO_PI))
        * canvasHeight * 0.04 * secondaryEnvelope
      break
    case 'vortex': {
      secondaryEnvelope = Math.sin(Math.PI * progress) * TRAVEL_PATH_STRENGTH
      const centerX = canvasWidth * 0.5
      const centerY = canvasHeight * 0.5
      const deltaX = baseX - centerX
      const deltaY = baseY - centerY
      const rotation = (1 - progress) * 2.5 + timeSeconds * 0.8 + phase * 0.15
      const cosine = Math.cos(rotation)
      const sine = Math.sin(rotation)
      const rotatedX = deltaX * cosine - deltaY * sine
      const rotatedY = deltaX * sine + deltaY * cosine
      const strength = secondaryEnvelope * 0.45
      x += (rotatedX - deltaX) * strength - deltaY * secondaryEnvelope * 0.08
      y += (rotatedY - deltaY) * strength + deltaX * secondaryEnvelope * 0.08
      break
    }
    case 'gravity': {
      // Preserve the established engine/export fall: particles start above the
      // canvas with a seeded stagger, accelerate quadratically, and converge
      // horizontally from a point 30% toward chaos. This defining path is
      // independent of the separate ambient-wiggle dampening control.
      const gravityStartX = safeLerp(input.orderX, input.chaosX, 0.3)
      const gravityX = safeLerp(gravityStartX, input.orderX, progress)
      const gravityStartY = -50 - delayRandom * 200
      const gravityProgress = progress * progress
      const gravityY = safeLerp(gravityStartY, input.orderY, gravityProgress)
      const gravityRetention = edgeRetention(progress)
      x = safeLerp(baseX, gravityX, gravityRetention)
      y = safeLerp(baseY, gravityY, gravityRetention)
      break
    }
    case 'bleed': {
      // Ink bleed: ink starts as tight blots near each owner region — the
      // kernel samples one particle at a time, so the blot sits most of the
      // way from chaos toward the particle's own order ink, keeping a word's
      // particles clustered near their region — then diffuses outward along
      // the transit. The diffusion curve rises to roughly 1.03 around
      // progress 0.68 and eases back to 1, giving a slight mid-overshoot past
      // the order point before settling. The edge envelope keeps the exact
      // chaos/order endpoints continuous.
      const blotTightness = 0.7 + 0.2 * unitHash(input.seed, particleIndex, 0x3c6e_f35f)
      const blotX = safeLerp(input.chaosX, input.orderX, blotTightness)
      const blotY = safeLerp(input.chaosY, input.orderY, blotTightness)
      const settle = progress - 1
      const diffusion = 1 + 1.9 * settle * settle * settle + 0.9 * settle * settle
      const bleedX = safeLerp(blotX, input.orderX, diffusion)
      const bleedY = safeLerp(blotY, input.orderY, diffusion)
      const bleedRetention = edgeRetention(progress)
      x = safeLerp(baseX, bleedX, bleedRetention)
      y = safeLerp(baseY, bleedY, bleedRetention)
      break
    }
    case 'sandstorm': {
      // Sandstorm: the chaos start is replaced by a leftward downwind
      // displacement with a seeded stagger (scheduling above), then gusts of
      // slow horizontal wind with faster vertical turbulence rock the ink
      // mid-transit. The edge envelope keeps both endpoints continuous.
      const gustRandom = unitHash(input.seed, particleIndex, 0x74fa_2c01)
      const stormPhase = unitHash(input.seed, particleIndex, 0x02e5_be93) * TWO_PI
      const stormStartX = input.chaosX - (0.3 + 0.5 * gustRandom) * canvasWidth
      const stormStartY = input.chaosY + (delayRandom - 0.5) * canvasHeight * 0.3
      const gustEnvelope = Math.sin(Math.PI * progress)
      const gustScale = Math.min(canvasWidth, canvasHeight)
      const stormX = safeLerp(stormStartX, input.orderX, smoothstep(localProgress))
        + Math.sin(timeSeconds * 1.7 + stormPhase) * gustScale * 0.08 * gustEnvelope
      const stormY = safeLerp(stormStartY, input.orderY, smoothstep(localProgress))
        + Math.cos(timeSeconds * 2.3 + stormPhase * 1.7) * gustScale * 0.04 * gustEnvelope
      const stormRetention = edgeRetention(progress)
      x = safeLerp(baseX, stormX, stormRetention)
      y = safeLerp(baseY, stormY, stormRetention)
      break
    }
    case 'magnet': {
      // Magnetic: ink starts on a hashed ray around its order point and rides
      // a tightening orbit — one and a half turns while the radius drains to
      // zero at progress 1. The edge envelope blends the ray start
      // continuously out of exact chaos.
      const rayReach = (0.2 + 0.3 * unitHash(input.seed, particleIndex, 0x51ed_270b))
        * Math.min(canvasWidth, canvasHeight)
      const orbitRadius = rayReach * (1 - progress)
      const orbitAngle = phase + progress * Math.PI * 3
      const magnetX = input.orderX + Math.cos(orbitAngle) * orbitRadius
      const magnetY = input.orderY + Math.sin(orbitAngle) * orbitRadius
      const magnetRetention = edgeRetention(progress)
      x = safeLerp(baseX, magnetX, magnetRetention)
      y = safeLerp(baseY, magnetY, magnetRetention)
      break
    }
    case 'rain': {
      // Rain settle: drops fall from above the canvas with a seeded start,
      // settle horizontally early, then overshoot and damped-bounce onto
      // their order ink. Like gravity, the edge envelope keeps both endpoints
      // continuous.
      const rainPhase = unitHash(input.seed, particleIndex, 0x02e5_be93) * TWO_PI
      const rainStartX = input.orderX + (rainPhase / TWO_PI - 0.5) * canvasWidth * 0.15
      const rainStartY = -30 - delayRandom * canvasHeight * 0.5
      const rainX = safeLerp(rainStartX, input.orderX, smoothstep(clamp01(progress * 1.6)))
      const rainY = safeLerp(rainStartY, input.orderY, dampedSpring(progress))
      const rainRetention = edgeRetention(progress)
      x = safeLerp(baseX, rainX, rainRetention)
      y = safeLerp(baseY, rainY, rainRetention)
      break
    }
    case 'breathing': {
      // A coherent pulse around the ink bounds, independent of idle wiggle.
      // Using target radii makes the effect readable even during travel.
      const centerX = input.textCenterX ?? canvasWidth * 0.5
      const centerY = input.textCenterY ?? canvasHeight * 0.5
      const livingEnvelope = smoothstep(progress)
      const pulse = Math.sin(timeSeconds * 2) * 0.035 * livingEnvelope
      x += (input.orderX - centerX) * pulse
      y += (input.orderY - centerY) * pulse
      break
    }
    case 'shimmer': {
      // A real RTL highlight band: opacity contrast carries the shimmer while
      // a small seeded lift keeps it legible on uniformly opaque particles.
      const sweepCycle = timeSeconds * 0.35
      const bandX = canvasWidth * (1 - (sweepCycle - Math.floor(sweepCycle)))
      const bandDistance = (input.orderX - bandX) / (canvasWidth * 0.18)
      const bandProximity = Math.exp(-bandDistance * bandDistance)
      const livingEnvelope = smoothstep(progress)
      alpha *= 1 - 0.42 * livingEnvelope * (1 - bandProximity)
      x += Math.cos(phase * 5) * bandProximity * livingEnvelope * 1.5
      y += Math.sin(phase * 7) * bandProximity * livingEnvelope * 3
      break
    }
    case 'flow': {
      // Flow follows a layout-provided local contour tangent. Older callers
      // get an honest RTL fallback instead of the unrelated chaos vector.
      const tangentX = input.tangentX ?? -1
      const tangentY = input.tangentY ?? 0
      const tangentLength = Math.hypot(tangentX, tangentY)
      const directionX = tangentLength > 1e-6 ? tangentX / tangentLength : -1
      const directionY = tangentLength > 1e-6 ? tangentY / tangentLength : 0
      const contourRank = input.ownerLetter >= 0 && input.letterCount > 0
        ? input.ownerLetter / Math.max(1, input.letterCount)
        : clamp01(input.orderX / canvasWidth)
      const drift = Math.sin(timeSeconds * 2.2 + contourRank * TWO_PI)
        * smoothstep(progress) * 10
      x += directionX * drift
      y += directionY * drift
      break
    }
    case 'linear':
    case 'cascade':
    case 'scatter':
    case 'elastic':
    case 'typewriter':
    case 'dots':
    case 'words':
      break
  }

  if (ambientWiggle > 0) {
    const wiggleTime = timeSeconds * input.wiggleSpeed * TWO_PI
    const wiggleEnvelope = smoothstep(progress) * ambientWiggle
    const phaseX = unitHash(input.seed, particleIndex, 0xa511_e9b3) * TWO_PI
    const phaseY = unitHash(input.seed, particleIndex, 0x63d8_35f1) * TWO_PI
    x += Math.sin(wiggleTime + phaseX) * wiggleEnvelope
    y += Math.cos(wiggleTime + phaseY) * wiggleEnvelope
  }

  assertFiniteFloat32(x, 'Animation output X')
  assertFiniteFloat32(y, 'Animation output Y')
  assertFiniteFloat32(localProgress, 'Animation output progress')
  assertFiniteFloat32(alpha, 'Animation output alpha')
  out.x = x
  out.y = y
  out.progress = localProgress
  writeAlpha(out, clamp01(alpha))
}
